import React, { useEffect, useState } from "react";
import { Edit2, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { addressLabel, addressPreset } from "@/lib/addressLabel";

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const emptyForm = () => ({
  id: null,
  preset: "home",
  customLabel: "",
  address: "",
  landmark: "",
  is_default: false,
  print_on_bills: false,
});

/**
 * The citizen's ONE address book. Each address carries its own "Print this address on my bills"
 * switch (default OFF) - that is the only place a citizen consents to a shop printing an address,
 * and it saves the instant it is flipped (with a toast). The add/edit form is one form with one
 * Save button.
 */
export function AddressBook() {
  const { t } = useApp();
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/addresses").then((r) => setAddresses(rows(r.data)));

  useEffect(() => {
    load().catch(() => setAddresses([]));
  }, []);

  const openEdit = (item) => {
    const found = addressPreset(item.label);
    const asPreset = found === "home" || found === "work" ? found : null;
    setForm({
      id: item.id,
      preset: asPreset || "other",
      customLabel: asPreset ? "" : item.label,
      address: item.address,
      landmark: item.landmark,
      is_default: item.is_default,
      print_on_bills: !!item.print_on_bills,
    });
  };

  const save = async () => {
    const label = form.preset === "other" ? form.customLabel.trim() : t(`addresses.label${capitalize(form.preset)}`);
    if (!label || form.address.trim().length < 3) return;
    setBusy(true);
    try {
      const payload = {
        label,
        address: form.address.trim(),
        landmark: form.landmark.trim(),
        is_default: form.is_default,
        print_on_bills: form.print_on_bills,
      };
      if (form.id) await api.patch(`/addresses/${form.id}`, payload);
      else await api.post("/addresses", payload);
      toast.success(t("addresses.saved"));
      setForm(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/addresses/${id}`);
      toast.success(t("addresses.deleted"));
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  // Instant save: flip the switch in place, roll back and say so if the server refuses.
  const togglePrint = async (item, next) => {
    setAddresses((prev) => prev.map((a) => (a.id === item.id ? { ...a, print_on_bills: next } : a)));
    try {
      await api.patch(`/addresses/${item.id}`, { print_on_bills: next });
      toast.success(t(next ? "addresses.printOnToast" : "addresses.printOffToast"));
    } catch (e) {
      setAddresses((prev) => prev.map((a) => (a.id === item.id ? { ...a, print_on_bills: !next } : a)));
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  return (
    <section className="rounded-2xl border border-border p-5 sm:p-6" data-testid="account-addresses-section">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
          <MapPin size={17} className="text-[hsl(var(--accent))]" /> {t("addresses.title")}
        </h2>
        {!form && (
          <Button size="sm" variant="outline" className="rounded-full" data-testid="address-add-btn" onClick={() => setForm(emptyForm())}>
            {t("addresses.add")}
          </Button>
        )}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{t("addresses.intro")}</p>

      <div className="mt-4 space-y-3" data-testid="account-address-list">
        {addresses.length === 0 && !form && <p className="text-sm text-muted-foreground">{t("addresses.empty")}</p>}
        {addresses.map((item) => (
          <div key={item.id} className="rounded-xl border border-border p-4" data-testid={`address-card-${item.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium">{addressLabel(item.label, t)}</p>
                  {item.is_default && <Badge className="bg-[hsl(var(--primary))]">{t("addresses.default")}</Badge>}
                </div>
                <p className="mt-1 break-words text-sm text-muted-foreground">{item.address}</p>
                {item.landmark && <p className="text-xs text-muted-foreground">{item.landmark}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" variant="ghost" aria-label={t("addresses.editAria")} data-testid={`address-edit-${item.id}`} onClick={() => openEdit(item)}>
                  <Edit2 size={14} />
                </Button>
                <Button size="sm" variant="ghost" aria-label={t("addresses.removeAria")} data-testid={`address-delete-${item.id}`} onClick={() => remove(item.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="mt-3 flex items-start justify-between gap-3 border-t border-border pt-3">
              <p className="text-sm font-medium">{t("addresses.printOnBills")}</p>
              <Switch
                aria-label={t("addresses.printOnBills")}
                data-testid={`address-print-${item.id}`}
                checked={!!item.print_on_bills}
                onCheckedChange={(next) => togglePrint(item, next)}
              />
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="mt-4 space-y-4 rounded-xl border border-border p-4" data-testid="address-form">
          <div className="flex flex-wrap gap-2">
            {["home", "work", "other"].map((preset) => (
              <button
                key={preset}
                type="button"
                data-testid={`address-label-${preset}`}
                onClick={() => setForm({ ...form, preset })}
                className={`rounded-full border px-3 py-1 text-xs ${
                  form.preset === preset ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
                }`}
              >
                {t(`addresses.label${capitalize(preset)}`)}
              </button>
            ))}
          </div>
          {form.preset === "other" && (
            <div className="space-y-1.5">
              <Label htmlFor="address-custom-label">{t("addresses.labelCustom")}</Label>
              <Input
                id="address-custom-label"
                data-testid="address-custom-label-input"
                value={form.customLabel}
                onChange={(e) => setForm({ ...form, customLabel: e.target.value })}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="address-field">{t("addresses.addressField")}</Label>
            <Textarea
              id="address-field"
              rows={2}
              data-testid="address-address-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="address-landmark">{t("addresses.landmarkField")}</Label>
            <Input
              id="address-landmark"
              data-testid="address-landmark-input"
              value={form.landmark}
              onChange={(e) => setForm({ ...form, landmark: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              data-testid="address-default-checkbox"
              checked={form.is_default}
              onCheckedChange={(checked) => setForm({ ...form, is_default: !!checked })}
            />
            {t("addresses.setDefault")}
          </label>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              data-testid="address-print-checkbox"
              checked={form.print_on_bills}
              onCheckedChange={(checked) => setForm({ ...form, print_on_bills: !!checked })}
            />
            <span>
              {t("addresses.printOnBills")}
              <span className="block text-xs text-muted-foreground">{t("addresses.printOnBillsHint")}</span>
            </span>
          </label>
          <div className="flex gap-2">
            <Button className="rounded-full" disabled={busy} data-testid="address-save-btn" onClick={save}>
              {busy ? t("common.saving") : t("addresses.save")}
            </Button>
            <Button variant="ghost" className="rounded-full" data-testid="address-cancel-btn" onClick={() => setForm(null)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
